// savegame.ts ↔ game/game.cpp SaveGame/PlaySavedGame + game/loadsave.cpp (the
// version gate) + game/sdl/savegame.cpp (the slot storage host layer) — the
// GAME-level savegame envelope over the sim core's byte-proven tail
// (Sim.saveState / Sim.load), plus save-slot management.
//
// Envelope layout (Game::SaveGame, game.cpp:2447-2493 — the first 3 entries are
// load-bearing for CheckSaveGameVersion() and DeleteStaleSaveGames()):
//   szVersion\0       gszVersion "1.8" (game.cpp:87, the SDL/web ship build)
//   byte nVerGameState  knVerGameState 9 (game.cpp:2395)
//   byte bPlatform      0 on every non-Palm platform (game.cpp:2468-2471)
//   PackId packid       raw struct image: dword id LE + byte hash[16]
//                       (mpshared/mpht.h:112-115; no padding)
//   szLvlFilename\0     m_miid.szLvlFilename (game/missionlist.h:19-22)
//   szNextLevel\0       m_szNextLevel (ht.h:2467)
//   <tail>              pvars + players + simulation (game.cpp:2483-2493)
//                       = Sim.saveState() byte-for-byte
//
// The storage layer (the POSIX temp-file rename dance of game/sdl/savegame.cpp)
// is the platform seam: an injected SaveStore keyed by slot number, with the
// in-memory default below (IndexedDB arrives with the browser shell). The
// filename encoding glue (htsave%d_<name>.bin, the ':'<->'#' mangle) has no
// analog — szName travels as data.
//
// The save-slot picker forms — PickLoadGameStream (loadsave.cpp:5-29),
// PickSaveGameStream (loadsave.cpp:31-45) and LoadGameForm (loadsave.cpp:108-208)
// — are M4 shell UI, as are the HtMessageBox failure arms noted inline.

import { Stream, MemoryStream } from '../../../sim/src/sm/stream.ts';
import { IniScanf } from '../../../sim/src/data/ini.ts';

// game/game.cpp:87 — the SDL/web ship build version string (DEV_BUILD stamps
// "+++VERSION+++"). The TS client (the primary, served at /) ships "1.9"; the
// legacy WASM build at /wasm is the older binary and still reports "1.8".
// This is the human-facing build version (title screen + savegame stamping) and
// is INDEPENDENT of knVersionSimulation (the MP/re-sim gate) — bumping it does
// not affect mixed TS/WASM multiplayer compatibility.
export const gszVersion = '1.9';

// game/game.cpp:2395
export const knVerGameState = 9;

// mpshared/mpht.h:45
export const kcbFilename = 29;

// game/ht.h:9050 — the slot of the temporary save written when the app
// suspends/reinitializes (Game::SaveReinitializeGame, game.cpp:1942-1964)
export const knGameReinitializeSave = -3;

// game/ht.h:9053 — the crash-recovery temp filename DeleteStaleSaveGames sweeps
export const kszTempName = 'htsavetemp';

// mpshared/mpht.h:110
export const PACKID_MAIN = 0xffffffff;

// mpshared/mpht.h:112-115
export interface PackId {
  id: number; // dword
  hash: Uint8Array; // byte hash[16]
}

// game/missionlist.h:19-22
export interface MissionIdentifier {
  packid: PackId;
  szLvlFilename: string; // char[kcbFilename]
}

// game/ht.h:8960-8965 struct Date — nMonth is tm_mon (0-based), as the C++
// stores it (game/sdl/savegame.cpp:205)
export interface WiDate {
  nYear: number;
  nMonth: number;
  nDay: number;
}

// ---- version parsing / compatibility -------------------------------------------

export interface ParsedVersion {
  nShipVersion: number;
  nMajorVersion: number;
  chMinorVersion: string; // '' ↔ the C++ NUL "no minor version"
}

// game/game.cpp:1391-1429
export function ParseVersion(pszVersion: string): ParsedVersion {
  // Parse the version.
  // A.B[c], for example 1.0a
  // A is ship version, B is major version, C is minor version

  // Scan for ship version. Allow 0., or no ship version specified
  let nShipVersion = 0;
  let ichAfterDot = 0;
  const r = IniScanf(pszVersion, '%d.%+');
  if (r.count === 0) {
    nShipVersion = 0;
    ichAfterDot = 0;
  } else {
    nShipVersion = r.values[0] as number;
    // a partial match ("18", no dot) leaves ichAfterDot at its initialized 0,
    // exactly as the C++ out-param does
    if (r.count >= 2) ichAfterDot = r.values[1] as number;
  }

  // Scan the major ("B") version: the full run of digits after the dot, as a PLAIN INTEGER, so
  // 1.8 < 1.9 < 1.10 < 1.11 … order correctly and the minor field isn't capped at .99.
  //
  // DELIBERATE DIVERGENCE from the C++ (game.cpp:1402-1413), which read only TWO digits as tens*ones:
  // "1.9" → 90, "1.8" → 80, but "1.10" → 10 — so 1.10 sorted BELOW 1.9 (and 1.8), a trap that blocks
  // any version past 1.9 in the 1.x line. This is the prerequisite for shipping 1.10+. It is safe:
  //   • it changes only ABSOLUTE values, never the RELATIVE order within a single build — and version
  //     compatibility (IsVersionCompatibleWithExe / CheckSaveGameVersion) always parses BOTH the save's
  //     string and this build's gszVersion with THIS function, so existing saves/levels load unchanged;
  //   • it is INDEPENDENT of knVersionSimulation (the MP / re-sim gate), so multiplayer and the bit-exact
  //     sim are unaffected — version strings never enter the sync hash;
  //   • the legacy WASM build at /wasm keeps the old C++ scheme (its binary is unchanged); the only
  //     cross-build version comparison is the one-way WASM→TS savegame migration, which still loads
  //     (an old "1.8" save is older than this build under either scheme).
  let nMajorVersion = 0;
  let ichAfterMajor = ichAfterDot;
  while (pszVersion[ichAfterMajor] >= '0' && pszVersion[ichAfterMajor] <= '9') {
    nMajorVersion = nMajorVersion * 10 + (pszVersion.charCodeAt(ichAfterMajor) - 0x30);
    ichAfterMajor++;
  }

  // Scan for minor version. Allow none
  let chMinorVersion = '';
  const ch = pszVersion[ichAfterMajor];
  if (ch !== undefined && ch >= 'a' && ch <= 'z') chMinorVersion = ch;

  return { nShipVersion, nMajorVersion, chMinorVersion };
}

// game/game.cpp:1494-1532 — chVersionCompareMinor is accepted but never compared
// ("Minor versions can be different."), as in the C++
export function IsVersionCompatibleWithExe(
  nVersionCompareShip: number,
  nVersionCompareMajor: number,
  chVersionCompareMinor: string,
  fUpwardCompatOK: boolean,
): boolean {
  void chVersionCompareMinor;

  // Unstamped EXEs can load any version database (game.cpp:1498 — the +1 dodges
  // the version stamper; never true for the "1.8" ship build, branch kept)
  if (gszVersion.slice(1).toLowerCase() === '++version+++') return true;

  const { nShipVersion: nVersionShip, nMajorVersion: nVersionMajor } = ParseVersion(gszVersion);

  if (fUpwardCompatOK) {
    // If ship version less, not ok
    if (nVersionShip < nVersionCompareShip) return false;

    // If ship version equal, check major
    if (nVersionShip === nVersionCompareShip) {
      if (nVersionMajor < nVersionCompareMajor) return false;
    }
  } else {
    // Ship versions must match
    if (nVersionShip !== nVersionCompareShip) return false;

    // Major versions must match too.
    if (nVersionMajor !== nVersionCompareMajor) return false;
  }

  // Minor versions can be different.
  return true;
}

// game/loadsave.cpp:80-102 — bPlatform is only checked on Palm (the PIL/PNO
// 68K↔ARM gate under #ifndef DEV_BUILD); every other platform ignores it
export function CheckSaveGameVersion(pszVersion: string, bPlatform: number): boolean {
  void bPlatform;

  // If not upwardly compatible with ship version and major version, delete the
  // save game. This is so v1.0a allows reading save game files from v1.0, etc.
  const { nShipVersion, nMajorVersion, chMinorVersion } = ParseVersion(pszVersion);
  return IsVersionCompatibleWithExe(nShipVersion, nMajorVersion, chMinorVersion, true);
}

// ---- the envelope ---------------------------------------------------------------

/** What Game::SaveGame serializes besides the stream: the mission identity +
 *  next-level Game members, and the deterministic tail (Sim.saveState). */
export interface SaveGameSource {
  miid: MissionIdentifier; // Game::m_miid (ht.h:2468)
  szNextLevel: string; // Game::m_szNextLevel (ht.h:2467)
  saveState(): Uint8Array; // pvars + players + simulation (game.cpp:2483-2493)
}

// game/game.cpp:2438-2507 — returns fSuccess (the C++ pstm==NULL →
// PickSaveGameStream arm is the M4 picker form; the !fSuccess HtMessageBox
// "Error saving game!..." is shell UI). Closes pstm, as the C++ does.
export function SaveGame(game: SaveGameSource, pstm: Stream): boolean {
  // Write build version string first
  // NOTE: these first 3 entries are assumed in CheckSaveGameVersion() and
  // DeleteStaleSaveGames()!!
  pstm.WriteString(gszVersion);

  // Save version
  pstm.WriteByte(knVerGameState);

  // Save platform byte. Needed for platform compatibility check (68K->ARM
  // transition). Whatever, doesn't matter since it isn't Palm (game.cpp:2471)
  pstm.WriteByte(0);

  // Save mission identifier — pstm->Write(&m_miid.packid, sizeof(m_miid.packid)):
  // the raw 20-byte struct image (dword id LE + byte hash[16])
  pstm.WriteDword(game.miid.packid.id);
  const hash = new Uint8Array(16);
  hash.set(game.miid.packid.hash.subarray(0, 16));
  pstm.Write(hash);
  pstm.WriteString(game.miid.szLvlFilename);

  // Save next level filename
  pstm.WriteString(game.szNextLevel);

  // Save pvars + player info + simulation (game.cpp:2483-2493 — the sim core's
  // byte-proven Sim.saveState tail)
  pstm.Write(game.saveState());

  // Check for error
  const fSuccess = pstm.IsSuccess();

  pstm.Close();
  return fSuccess;
}

/** The parsed envelope: PlaySavedGame's version gate + PlayLevel's saved-game
 *  reads. The stream is left positioned at the pvars tail — Sim.load's input. */
export interface SaveGameEnvelope {
  szVersion: string;
  miid: MissionIdentifier;
  szNextLevel: string;
}

// game/game.cpp:2396-2436 + the PlayLevel saved-game envelope reads
// (game.cpp:1583-1592). Returns null where the C++ returns knGoFailure (the
// HtMessageBox "Save game out of date!" is shell UI); on success the C++ hands
// the stream to PlayLevel → RunSimulation — in TS that handoff is
// Sim.load({ save: <the remaining bytes> }), which consumes the pvars + players
// + simulation tail exactly as game.cpp:1596/1648/RunSimulation do.
export function PlaySavedGame(pstm: Stream): SaveGameEnvelope | null {
  // Overall, look at build version first
  const szVersion = pstm.ReadString(32);

  // Read version
  let fStale = false;
  const nVer = pstm.ReadByte();
  if (nVer !== knVerGameState) fStale = true;

  // Read platform
  if (!fStale) {
    let bPlatform = 0;
    if (nVer >= 6) bPlatform = pstm.ReadByte();

    // Check version
    if (!CheckSaveGameVersion(szVersion, bPlatform)) fStale = true;
  }

  // If old, bail
  if (fStale) {
    pstm.Close();
    return null;
  }

  // Load MissionIdentifier (game.cpp:1586-1588)
  const id = pstm.ReadDword();
  const hash = new Uint8Array(16);
  hash.set(pstm.Read(16));
  const szLvlFilename = pstm.ReadString(kcbFilename);

  // Load next level filename (game.cpp:1592)
  const szNextLevel = pstm.ReadString(kcbFilename);

  return { szVersion, miid: { packid: { id, hash }, szLvlFilename }, szNextLevel };
}

// ---- save-slot storage (game/sdl/savegame.cpp behind the SaveStore seam) ---------

export interface SaveGameMeta {
  nGame: number;
  szName: string; // HostNewSaveGameStream's pszName (the level title)
  msDate: number; // epoch ms — the C++ reads the file mtime (sdl/savegame.cpp:196-206)
}

export interface SaveGameRecord extends SaveGameMeta {
  bytes: Uint8Array;
}

/** The injected storage seam replacing the POSIX saves directory: slots are
 *  keyed by nGame directly (no filenames, no temp-rename dance). */
export interface SaveStore {
  get(nGame: number): SaveGameRecord | null;
  put(rec: SaveGameRecord): void;
  delete(nGame: number): boolean;
  list(): SaveGameMeta[];
}

/** The default in-memory SaveStore (IndexedDB lands with the browser shell). */
export class MemorySaveStore implements SaveStore {
  private saves = new Map<number, SaveGameRecord>();

  get(nGame: number): SaveGameRecord | null {
    return this.saves.get(nGame) ?? null;
  }

  put(rec: SaveGameRecord): void {
    this.saves.set(rec.nGame, rec);
  }

  delete(nGame: number): boolean {
    return this.saves.delete(nGame);
  }

  list(): SaveGameMeta[] {
    return [...this.saves.values()].map(({ nGame, szName, msDate }) => ({ nGame, szName, msDate }));
  }
}

// FileStream::Close over the new-save temp file (sdl/savegame.cpp:62-77 via
// :255-272): ONLY a successful save replaces the slot — the temp is renamed over
// the old save; on failure the old save survives untouched.
class NewSaveGameStream extends MemoryStream {
  private store: SaveStore;
  private nGame: number;
  private szName: string;

  constructor(store: SaveStore, nGame: number, szName: string) {
    super();
    this.store = store;
    this.nGame = nGame;
    this.szName = szName;
  }

  override Close(): void {
    if (this.IsSuccess()) {
      this.store.put({
        nGame: this.nGame,
        szName: this.szName,
        msDate: Date.now(),
        bytes: this.bytes(),
      });
    }
  }
}

// FileStream::Close over the temp-renamed open save (sdl/savegame.cpp:281-303):
// the file was renamed to htsavetemp before reading. On success it is unlinked
// (fDelete) or renamed back into its slot (!fDelete); on FAILURE it stays
// stranded as htsavetemp — lost from its slot either way except success+keep.
class OpenSaveGameStream extends MemoryStream {
  private store: SaveStore;
  private nGame: number;
  private fDelete: boolean;

  constructor(store: SaveStore, nGame: number, fDelete: boolean, bytes: Uint8Array) {
    super(bytes);
    this.store = store;
    this.nGame = nGame;
    this.fDelete = fDelete;
  }

  override Close(): void {
    if (this.IsSuccess()) {
      if (this.fDelete) this.store.delete(this.nGame); // unlink(szTempName)
    } else {
      this.store.delete(this.nGame); // stranded as htsavetemp (swept at startup)
    }
  }
}

// game/sdl/savegame.cpp:226-273
export function HostNewSaveGameStream(
  store: SaveStore,
  nGame: number,
  pszName: string,
): Stream | null {
  // the old save for this slot is deleted only when the new one lands (Close);
  // the ':'→'#' filename mangle (:243-250) is encoding glue with no analog
  return new NewSaveGameStream(store, nGame, pszName);
}

// game/sdl/savegame.cpp:275-304 (the fDelete default is ht.h:9057)
export function HostOpenSaveGameStream(
  store: SaveStore,
  nGame: number,
  fDelete = false,
): Stream | null {
  const rec = store.get(nGame);
  if (rec === null) return null;
  return new OpenSaveGameStream(store, nGame, fDelete, rec.bytes);
}

/** Returning to the menu means no game is in progress: drop the resume/reinitialize
 *  slot (knGameReinitializeSave) so a page refresh at the title does NOT drop back
 *  into a finished mission (the user-reported "a refresh resurrected an old game" bug;
 *  shell.cpp's LaunchLoop did this for the canvas shell, shell.ts:307).
 *
 *  This is the extracted, testable core of web/main.ts onGameGo's return-to-menu path.
 *  `fResumingSavedGame` is TRUE only when the outcome is an in-game "Load Game"
 *  (knGoLoadSavedGame) WITH a pending saved-game stream — onGameGo re-launches that
 *  stream instead of returning to the menu, so the slot must NOT be cleared (the next
 *  autosave tick re-writes it). Every other outcome (win/lose/abort/load-failure/stop)
 *  reaches the menu, so the slot is dropped. Deletes DIRECTLY (not open-with-delete) so
 *  an IDB-only slot with no in-memory record is cleared too (IdbSaveStore.delete writes
 *  the delete through unconditionally — see stores.ts).
 *
 *  Returns true iff it cleared the slot. */
export function ClearResumeSlotOnReturnToMenu(
  store: SaveStore,
  fResumingSavedGame: boolean,
): boolean {
  if (fResumingSavedGame) return false;
  store.delete(knGameReinitializeSave);
  return true;
}

// game/sdl/savegame.cpp:306-328 — PRESERVED QUIRK: only the psz arm unlinks.
// With psz == NULL the found slot file is never deleted (the C++ falls through
// to `return false` without an unlink), so stale saves SURVIVE
// DeleteStaleSaveGames on this platform and are merely re-rejected at load.
export function HostDeleteSaveGame(store: SaveStore, psz: string | null, nGame: number): boolean {
  if (psz === null) {
    if (store.get(nGame) === null) return false;
  }

  if (psz !== null) {
    // the named arm only ever targets kszTempName (loadsave.cpp:77) —
    // crash-recovery temp files, which don't exist in the SaveStore model
    return true;
  }
  return false;
}

// game/sdl/savegame.cpp:174-179 via FindSaveGame's slot count (:134-171): every
// occupied slot counts except the temporary "Reinitialize" save
export function HostGetSaveGameCount(store: SaveStore): number {
  return store.list().filter((m) => m.nGame !== knGameReinitializeSave).length;
}

export interface SaveGameNameInfo {
  szName: string;
  date: WiDate;
  nHours24: number;
  nMinutes: number;
  nSeconds: number;
}

// game/misc.cpp:850-876 — CompareDates (the LoadGameForm last-save scan)
export function CompareDates(pdate1: WiDate, pdate2: WiDate): number {
  // Check years

  if (pdate1.nYear < pdate2.nYear) return -1;
  if (pdate1.nYear > pdate2.nYear) return 1;

  // Years are equal. Check months.

  if (pdate1.nMonth < pdate2.nMonth) return -1;
  if (pdate1.nMonth > pdate2.nMonth) return 1;

  // Months are equal. Check days

  if (pdate1.nDay < pdate2.nDay) return -1;
  if (pdate1.nDay > pdate2.nDay) return 1;

  // All equal

  return 0;
}

// game/sdl/savegame.cpp:181-224 — null ↔ the C++ false arm (which fills psz with
// "-- Empty --" for the M4 LoadGameForm to render). The name prefix-stripping +
// '#'→':' restore (:208-221) is filename encoding glue with no analog.
export function HostGetSaveGameName(store: SaveStore, nGame: number): SaveGameNameInfo | null {
  const meta = store.list().find((m) => m.nGame === nGame);
  if (meta === undefined) return null;

  // the C++ reads the file mtime via stat + localtime (:196-206); nMonth stays
  // 0-based (tm_mon) and nYear full (tm_year + 1900)
  const d = new Date(meta.msDate);
  return {
    szName: meta.szName,
    date: { nYear: d.getFullYear(), nMonth: d.getMonth(), nDay: d.getDate() },
    nHours24: d.getHours(),
    nMinutes: d.getMinutes(),
    nSeconds: d.getSeconds(),
  };
}

// game/loadsave.cpp:47-78 — sweeps slot knGameReinitializeSave then 0..19,
// version-gating each occupied slot. NOTE the HostDeleteSaveGame(NULL, nGame)
// call is the preserved no-unlink quirk above: on the SDL/web platform stale
// saves are detected but never actually removed.
export function DeleteStaleSaveGames(store: SaveStore): void {
  for (let nGame = knGameReinitializeSave; nGame < 20;) {
    const pstm = HostOpenSaveGameStream(store, nGame, false);
    if (pstm !== null) {
      const szVersion = pstm.ReadString(32);
      const bVer = pstm.ReadByte();
      let bPlatform = 0;
      if (bVer >= 6) bPlatform = pstm.ReadByte();
      pstm.Close();

      if (!CheckSaveGameVersion(szVersion, bPlatform)) {
        HostDeleteSaveGame(store, null, nGame);
      }
    }

    // Ugly
    if (nGame === knGameReinitializeSave) {
      nGame = 0;
    } else {
      nGame++;
    }
  }

  // delete any crashed save games
  HostDeleteSaveGame(store, kszTempName, 0);
}
